import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';

import { IEmployee } from 'src/app/models/employee.model';
import { AuthService } from 'src/app/services/auth.service';
import { ToastService } from 'src/app/services/toast.service';
import { HelperService } from 'src/app/services/helper.service';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { EClothingSize, EShoeSize } from 'src/app/enum/size.enum';
import { NgForm } from '@angular/forms';
import { cloneDeep, isEqual } from 'lodash-es';
import { EmployeeService } from 'src/app/services/employee.service';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';

@Component({
  selector: 'app-personnel-file',
  templateUrl: './personnel-file.component.html',
  styleUrl: './personnel-file.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PersonnelFileComponent implements OnInit {
  @ViewChild('shoeSizeDropdown') shoeSizeDropdown;
  @ViewChild('clothingSizeDropdown') clothingSizeDropdown;

  public employeeForm: FormGroup;
  public passwordForm: FormGroup;

  public employee = {} as IEmployee;
  public clonedEmployee = {} as IEmployee;
  public clothingSizes = Object.values(EClothingSize);
  public shoeSizes = Object.values(EShoeSize);

  private subscriptions: Subscription = new Subscription();
  public birthday: string;
  public iban: string;

  constructor(
    private authService: AuthService,
    private employeeService: EmployeeService,
    private toastService: ToastService,
    public helperService: HelperService,
    private router: Router,
    private fb: FormBuilder,
  ) {
    this.employee = this.authService.getCurrentEmployee();
    this.clonedEmployee = cloneDeep(this.employee);
  }

  ngOnInit(): void {
    // Format IBAN if it exists
    if (this.employee.iban) {
      this.employee.iban = this.formatIban(this.employee.iban);
    }

    this.initForms();
    this.birthday = this.formatDate(new Date(this.employee.birthdate));
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private formatIban(iban: string): string {
    return iban
      .replace(/[^\dA-Z]/g, '')
      .replace(/(.{4})/g, '$1 ')
      .trim()
      .toUpperCase();
  }

  public formatIbanInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Store cursor position
    const cursorPosition = input.selectionStart;
    const lengthBefore = value.length;

    // Format the IBAN
    const formattedValue = this.formatIban(value);

    // Only update if the formatted value is different
    if (formattedValue !== value) {
      // Update the form control value
      this.employeeForm.get('iban').setValue(formattedValue, { emitEvent: false });

      // Calculate new cursor position (accounting for added spaces)
      const addedSpaces = formattedValue.length - value.replace(/ /g, '').length;
      const newPosition = Math.min(cursorPosition + (formattedValue.length - lengthBefore), formattedValue.length);

      // Restore cursor position after the DOM updates
      setTimeout(() => {
        input.setSelectionRange(newPosition, newPosition);
      }, 0);
    }
  }

  private updateEmployeeFromForm(): void {
    // Update employee object with form values
    this.employee.firstname = this.employeeForm.get('firstname').value;
    this.employee.lastname = this.employeeForm.get('lastname').value;
    this.employee.address = this.employeeForm.get('address').value;
    this.employee.city = this.employeeForm.get('city').value;
    this.employee.postcode = this.employeeForm.get('postcode').value;
    this.employee.clothingSize = this.employeeForm.get('clothingSize').value;
    this.employee.shoeSize = this.employeeForm.get('shoeSize').value;
    this.employee.iban = this.employeeForm.get('iban').value;
    // Birthday is handled separately through the onSelectBirthday method
  }

  private initForms(): void {
    this.employeeForm = this.fb.group({
      firstname: [this.employee.firstname, Validators.required],
      lastname: [this.employee.lastname, Validators.required],
      address: [this.employee.address, [Validators.required, Validators.pattern(/\d+/)]], // contain a housenumber
      city: [this.employee.city, [Validators.required, Validators.pattern(/^\w/)]], // must start with a letter
      postcode: [this.employee.postcode, [Validators.required, Validators.pattern(/^[0-9]{4,5}$/)]], // allow AT and DE PLZ
      clothingSize: [this.employee.clothingSize, Validators.required],
      birthday: [this.employee.birthdate, Validators.required],
      shoeSize: [this.employee.shoeSize, Validators.required],
      iban: [
        this.employee.iban,
        [Validators.required, Validators.pattern(/^[A-Z]{2}\d{2}[A-Z0-9 ]{1,34}$/i)], // allow international IBAN with spaces
      ],
    });

    this.passwordForm = this.fb.group(
      {
        oldPassword: [''],
        password: ['', [Validators.minLength(6), Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d$@!%*?&]+$/)]],
        confirmPassword: ['', []],
      },
      {
        validators: this.passwordMatchValidator,
      },
    );
  }

  public onSubmit(): void {
    if (this.employee.firstLogin && this.passwordForm.value.password.length === 0) {
      this.toastService.error('You have to set a new password on first login');
      return;
    }
    if (this.employeeForm.invalid || this.passwordForm.invalid) {
      this.toastService.error('Form is invalid, Please fill all fields');
      return;
    }

    // Update employee object with form values
    this.updateEmployeeFromForm();

    if (isEqual(this.employee, this.clonedEmployee) && !this.passwordForm.value.password) {
      this.toastService.success('No changes were made');
      return;
    }

    // Create a copy of the employee for submission
    const employeeForSubmission = cloneDeep(this.employee);

    // Remove spaces from IBAN for backend processing if needed
    if (employeeForSubmission.iban) {
      employeeForSubmission.iban = employeeForSubmission.iban.replace(/\s/g, '');
    }

    this.employeeService
      .editPersonalInformation(
        employeeForSubmission,
        this.passwordForm.value.oldPassword,
        this.passwordForm.value.password,
      )
      .subscribe({
        next: (res) => {
          // Format the IBAN in the response data if it exists
          if (res.DATA.iban) {
            res.DATA.iban = this.formatIban(res.DATA.iban);
          }

          this.authService.setCurrentEmployee(res.DATA);
          this.toastService.success('Your personal information has been updated');
          this.passwordForm.reset();
          if (this.employee.firstLogin) {
            this.router.navigate(['/dashboard']);
          }
        },
        error: (err) => {
          this.toastService.error(err.error.DATA);
        },
      });
  }

  public onSelectClothingSize(size: EClothingSize) {
    this.employeeForm.get('clothingSize').setValue(size);
    this.employee.clothingSize = size;
  }

  public onSelectShoeSize(size: EShoeSize) {
    this.employeeForm.get('shoeSize').setValue(size);
    this.employee.shoeSize = size;
  }

  public onSelectBirthday(event: Event) {
    const birthday = this.helperService.dateToISOString(event);
    this.employee.birthdate = birthday;

    this.employeeForm.get('birthday').setValue((event.target as HTMLInputElement).value);
  }

  public closeAllDropdowns(dropdown: string) {
    if (dropdown !== 'shoeSizeDropdown' && this.shoeSizeDropdown) {
      this.shoeSizeDropdown.showDropdown = false;
    }
    if (dropdown !== 'clothingSizeDropdown' && this.clothingSizeDropdown) {
      this.clothingSizeDropdown.showDropdown = false;
    }
  }

  private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

    return password === confirmPassword ? null : { notMatching: true };
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
